
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Settings } from "lucide-react";
import { useAppSettings } from "@/hooks/useAppSettings";
import { useTheme } from "@/hooks/use-theme";
import { ThemeConfigurationCard } from "./shared/ThemeConfigurationCard";
import { toast } from "sonner";

const SettingsManager = () => {
  const { settings, loading, updateSetting } = useAppSettings();
  const { primaryColor, setPrimaryColor, loading: themeLoading } = useTheme();
  const [saving, setSaving] = useState(false);

  // Extended theme configuration to match organization settings
  const [themeConfig, setThemeConfig] = useState({
    primary_color: '#3B82F6',
    secondary_color: '#1E40AF',
    accent_color: '#10B981',
    warning_color: '#F59E0B',
    error_color: '#EF4444',
    border_radius: 'medium',
    font_scale: 'normal',
  });

  useEffect(() => {
    if (!themeLoading && primaryColor) {
      setThemeConfig(prev => ({
        ...prev,
        primary_color: primaryColor,
        // TODO: Load other theme settings from global settings once implemented
      }));
    }
  }, [primaryColor, themeLoading]);

  const handleThemeChange = (newThemeConfig: typeof themeConfig) => {
    setThemeConfig(newThemeConfig);
  };

  const handleSaveTheme = async () => {
    setSaving(true);
    try {
      // For now, only save the primary color until other global theme settings are implemented
      await setPrimaryColor(themeConfig.primary_color);
      
      // TODO: Implement saving of other theme settings to global configuration
      // This will require extending the useTheme hook and app settings
      
      toast.success("System default theme updated successfully");
    } catch (error) {
      console.error('Error updating theme:', error);
      toast.error("Failed to update system default theme");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ThemeConfigurationCard
        title="System Default Theme"
        description="Configure default theme settings that apply system-wide and serve as fallback for organizations without custom themes."
        themeConfig={themeConfig}
        onThemeChange={handleThemeChange}
        onSave={handleSaveTheme}
        loading={themeLoading}
        saving={saving}
        isSystemDefault={true}
      />
      
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Application Settings
          </CardTitle>
          <CardDescription>
            Configure system-wide application settings
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Additional system configuration options will appear here.
            </p>
            {/* Future settings can be added here */}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsManager;
